import {
  collection,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  getCountFromServer,
  runTransaction,
  Timestamp,
  startAfter,
  DocumentData,
  QueryDocumentSnapshot,
  QueryConstraint,
  doc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Activity, ActivityType, EntityType, ActivityMetadata, ActivityFilters, ActivityListResult } from '../types/activity';
import { errorService } from './errorService';

export class ActivityService {
  private static readonly COLLECTION = 'agentActivities';
  private static readonly MAX_DAYS = 30; // Only keep activities for 30 days
  /**
   * Hard cap on documents pulled into the browser when an in-memory search term
   * is applied. Firestore cannot do substring matching, so the search has to
   * scan - but it must never scan without a bound.
   */
  private static readonly SEARCH_SCAN_LIMIT = 500;

  /**
   * Deterministic document id for an activity.
   *
   * One row per (agent, activityType, entityType, entity). Using a derived id
   * instead of addDoc() is what makes logActivity idempotent: two tabs logging
   * the same view converge on the same document rather than racing a
   * read-then-delete-then-add cycle that produced the very duplicates it was
   * trying to remove.
   */
  private static buildActivityId(
    agentId: string,
    activityType: ActivityType,
    entityType: EntityType,
    entityId: string
  ): string {
    // Firestore document ids may not contain '/'. Entity ids are Firestore ids
    // or uids so this is defensive only.
    const safe = (part: string) => part.replace(/\//g, '_');
    return [safe(agentId), safe(activityType), safe(entityType), safe(entityId)].join('__');
  }

  /**
   * Log a new activity for an agent
   * CRITICAL: Always pass agentId to ensure proper agent-specific tracking
   *
   * Atomic and idempotent: the document id is derived from the activity's
   * identity and the write runs inside a transaction, so concurrent tabs cannot
   * create duplicates and no delete fan-out is needed.
   */
  static async logActivity(
    agentId: string,
    activityType: ActivityType,
    entityType: EntityType,
    entityId: string,
    entityName: string,
    metadata?: ActivityMetadata
  ): Promise<void> {
    try {
      if (!agentId) {
        console.error('ActivityService: Cannot log activity without agentId');
        return;
      }

      const activityRef = doc(
        db,
        this.COLLECTION,
        this.buildActivityId(agentId, activityType, entityType, entityId)
      );

      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(activityRef);
        const now = Timestamp.now();

        if (existing.exists()) {
          // Refresh the occurrence; keep the original createdAt.
          transaction.update(activityRef, {
            entityName,
            metadata: metadata || {},
            timestamp: now
          });
        } else {
          transaction.set(activityRef, {
            agentId,
            activityType,
            entityType,
            entityId,
            entityName,
            metadata: metadata || {},
            timestamp: now,
            createdAt: now
          });
        }
      });
    } catch (error) {
      console.error('ActivityService: Error logging activity:', error);
      // Don't throw - activity logging should not break the user experience
      await errorService.logError(
        error instanceof Error ? error : new Error('Failed to log activity'),
        undefined,
        'low',
        { agentId, activityType, entityType, entityId }
      );
    }
  }

  /**
   * Get recent activities for a specific agent
   * CRITICAL: Always filters by agentId
   */
  static async getRecentActivities(agentId: string, limit: number = 10): Promise<Activity[]> {
    try {
      if (!agentId) {
        console.error('ActivityService: Cannot get activities without agentId');
        return [];
      }

      const q = query(
        collection(db, this.COLLECTION),
        where('agentId', '==', agentId),
        orderBy('timestamp', 'desc'),
        firestoreLimit(limit)
      );

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          agentId: data.agentId,
          activityType: data.activityType,
          entityType: data.entityType,
          entityId: data.entityId,
          entityName: data.entityName,
          metadata: data.metadata || {},
          timestamp: data.timestamp?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date()
        } as Activity;
      });
    } catch (error) {
      console.error('ActivityService: Error getting recent activities:', error);
      await errorService.logError(
        error instanceof Error ? error : new Error('Failed to get recent activities'),
        undefined,
        'medium',
        { agentId, limit }
      );
      return [];
    }
  }

  /** Start of the local day containing `date`. */
  private static startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /** End of the local day containing `date`, inclusive to the millisecond. */
  private static endOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private static toActivity(docSnapshot: QueryDocumentSnapshot<DocumentData>): Activity {
    const data = docSnapshot.data();
    return {
      id: docSnapshot.id,
      agentId: data.agentId,
      activityType: data.activityType,
      entityType: data.entityType,
      entityId: data.entityId,
      entityName: data.entityName,
      metadata: data.metadata || {},
      timestamp: data.timestamp?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date()
    } as Activity;
  }

  /**
   * Get all activities for a specific agent with filters and pagination
   * CRITICAL: Always filters by agentId
   *
   * `total` is the real number of matching records, not the size of the page.
   * When a searchTerm is supplied the match runs across a bounded scan of the
   * filtered set (Firestore has no substring operator), so search results are
   * not limited to whatever happened to land on the current page.
   */
  static async getAllActivities(
    agentId: string,
    filters?: ActivityFilters,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<ActivityListResult> {
    try {
      if (!agentId) {
        console.error('ActivityService: Cannot get activities without agentId');
        return { activities: [], total: 0, hasMore: false };
      }

      // Filters that define the matching set. Shared by the page query and the
      // count query so the reported total always matches what is listed.
      const filterConstraints: QueryConstraint[] = [
        where('agentId', '==', agentId)
      ];

      if (filters?.activityType) {
        filterConstraints.push(where('activityType', '==', filters.activityType));
      }

      if (filters?.entityType) {
        filterConstraints.push(where('entityType', '==', filters.entityType));
      }

      // Date range. Date-picker values arrive at local midnight, so an unadjusted
      // endDate excluded the whole final day the user selected.
      if (filters?.startDate) {
        filterConstraints.push(
          where('timestamp', '>=', Timestamp.fromDate(this.startOfDay(filters.startDate)))
        );
      }
      if (filters?.endDate) {
        filterConstraints.push(
          where('timestamp', '<=', Timestamp.fromDate(this.endOfDay(filters.endDate)))
        );
      } else if (!filters?.startDate) {
        // Default window: the last MAX_DAYS whole local days. Date arithmetic is
        // done on a Date (an absolute instant) and converted by
        // Timestamp.fromDate, so the stored UTC values compare correctly; the
        // normalization here just aligns the window to a day boundary.
        const windowStart = this.startOfDay(new Date());
        windowStart.setDate(windowStart.getDate() - this.MAX_DAYS);
        filterConstraints.push(where('timestamp', '>=', Timestamp.fromDate(windowStart)));
      }

      const pageLimit = filters?.limit || 20;
      const searchTerm = filters?.searchTerm?.trim().toLowerCase();

      if (searchTerm) {
        // Scan the filtered set (bounded), match in memory, THEN page.
        const searchQuery = query(
          collection(db, this.COLLECTION),
          ...filterConstraints,
          orderBy('timestamp', 'desc'),
          firestoreLimit(this.SEARCH_SCAN_LIMIT)
        );
        const searchSnapshot = await getDocs(searchQuery);

        const matches = searchSnapshot.docs
          .map(docSnapshot => this.toActivity(docSnapshot))
          .filter(activity => activity.entityName?.toLowerCase().includes(searchTerm));

        return {
          activities: matches.slice(0, pageLimit),
          total: matches.length,
          hasMore: matches.length > pageLimit
        };
      }

      const pageConstraints: QueryConstraint[] = [
        ...filterConstraints,
        orderBy('timestamp', 'desc'),
        firestoreLimit(pageLimit + 1) // one extra to detect a further page
      ];

      if (lastDoc) {
        pageConstraints.push(startAfter(lastDoc));
      }

      const [snapshot, countSnapshot] = await Promise.all([
        getDocs(query(collection(db, this.COLLECTION), ...pageConstraints)),
        getCountFromServer(query(collection(db, this.COLLECTION), ...filterConstraints))
      ]);

      const activities = snapshot.docs
        .slice(0, pageLimit)
        .map(docSnapshot => this.toActivity(docSnapshot));

      return {
        activities,
        total: countSnapshot.data().count,
        hasMore: snapshot.docs.length > pageLimit
      };
    } catch (error) {
      console.error('ActivityService: Error getting all activities:', error);
      await errorService.logError(
        error instanceof Error ? error : new Error('Failed to get all activities'),
        undefined,
        'medium',
        { agentId }
      );
      return { activities: [], total: 0, hasMore: false };
    }
  }

  /**
   * Helper method to get activity icon and color based on type
   */
  static getActivityIcon(activityType: ActivityType): { icon: string; color: string } {
    const iconMap: Record<ActivityType, { icon: string; color: string }> = {
      customer_viewed: { icon: 'pi-user', color: '#2ecc71' },
      customer_edited: { icon: 'pi-user-edit', color: '#2ecc71' },
      insight_viewed: { icon: 'pi-lightbulb', color: '#f39c12' },
      insight_status_changed: { icon: 'pi-check-circle', color: '#3498db' },
      notification_viewed: { icon: 'pi-bell', color: '#9b59b6' },
      agent_viewed: { icon: 'pi-users', color: '#16a085' }
    };

    return iconMap[activityType] || { icon: 'pi-circle', color: '#95a5a6' };
  }

  /**
   * Helper method to format activity description
   */
  static formatActivityDescription(activity: Activity): string {
    switch (activity.activityType) {
      case 'customer_viewed':
        return `Viewed customer ${activity.entityName}`;
      case 'customer_edited':
        return `Updated customer ${activity.entityName}`;
      case 'insight_viewed':
        return `Reviewed insight: ${activity.entityName}`;
      case 'insight_status_changed':
        const oldStatus = activity.metadata?.oldStatus || 'unknown';
        const newStatus = activity.metadata?.newStatus || 'unknown';
        return `Changed insight "${activity.entityName}" from ${oldStatus} to ${newStatus}`;
      case 'notification_viewed':
        return `Read notification: ${activity.entityName}`;
      case 'agent_viewed':
        return `Viewed agent profile: ${activity.entityName}`;
      default:
        return `Interacted with ${activity.entityName}`;
    }
  }
}

